import {
  APPLICATION_NAMES,
  ENCOMPASS_URL,
  EPM_URL,
  PeConnAccessToken,
  REFRESHER,
} from '@share/constants';
import { RefresherStatus } from './data-services/refresher-status.enum';
import { Router, RoutesRecognized } from '@angular/router';
import { AuthenticationManager } from './core/oauth/authentication-manager.service';
import { Subscription } from 'rxjs';
import {
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  HostListener,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NGXLogger } from 'ngx-logger';
import { EnvironmentService } from './core/services/environment.service';

@Component({
  selector: 'conn-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private authenticationManager = inject(AuthenticationManager);
  private sanitizer = inject(DomSanitizer);
  private logger = inject(NGXLogger);
  private ngZone = inject(NgZone);
  private envService = inject(EnvironmentService);

  readonly APPLICATION_NAMES = APPLICATION_NAMES;
  logoutText =
    'You have been logged out. You can close this tab or click the button below to login again.';
  hideLoginButton = false;
  version = this.envService.getVersion();
  title = 'Patient Engagement Management';
  isLoading = true;
  broadcastChannel: BroadcastChannel | null = null;
  subscriptionRefresh: Subscription | null = null;
  logoutUrl: SafeResourceUrl;
  accessToken: PeConnAccessToken;
  isLoggingOut = false;
  isOnErrorPage = false;
  isEyefinityApp = false;
  showPanel = false;
  routedFromEpm: boolean;
  isUserLoggedIn: boolean;
  hideFooter: boolean;
  private switchedTokenSubscription: Subscription | null = null;
  private readonly onEyefinityLogoutMessage = (e: MessageEvent) => {
    if (e?.data?.type === 'logout') {
      this.authenticationManager.logout();
    }
  };

  ngOnInit() {
    this.isUserLoggedIn = false;
    this.broadcastChannel = new BroadcastChannel(REFRESHER);
    this.loadDynamicCss();
    this.logoutUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.envService.getLogoutUrl(),
    );
    this.switchedTokenSubscription = this.authenticationManager
      .getSwitchedToken()
      .subscribe((newToken) => {
        if (!!newToken) {
          this.accessToken = newToken;
        }
      });
    this.accessToken = this.authenticationManager.getAccessToken();

    // removing login button for encompass users and changing logout text accordingly
    if (this.accessToken.application === APPLICATION_NAMES.encompass) {
      this.logoutText = 'You have been logged out. You can close this tab.';
      this.hideLoginButton = true;
    }

    this.isEyefinityApp =
      this.accessToken.application === APPLICATION_NAMES.eyefinity;
    // adding logic to handle logout in eyefinity.com
    if (this.isEyefinityApp) {
      window.addEventListener('message', this.onEyefinityLogoutMessage);
    }
    if (this.isEyefinityApp && this.accessToken.signupStatusEwx !== 'Manage') {
      this.hideFooter = true;
    }
    this.broadcastChannel.onmessage = (e) => {
      if (e?.data) {
        this.logger.info(
          'Received message from broadcast channel in app component on app state: ' +
            e.data +
            ' and this is the user state:' +
            this.isUserLoggedIn,
        );
        if (e.data === RefresherStatus.loading) {
          this.ngZone.run(() => {
            this.isLoading = true;
          });
        }
        if (e.data === RefresherStatus.done) {
          this.ngZone.run(() => {
            this.isLoading = false;
            this.isUserLoggedIn = true;
          });
        }
      }
    };
    this.router.events.subscribe((val) => {
      this.routedFromEpm = this.isRoutingFromEpm();
      if (val instanceof RoutesRecognized) {
        if (!this.subscriptionRefresh) {
          this.subscriptionRefresh = this.authenticationManager
            .refreshAuthToken(val.state.root)
            .subscribe(
              (result) => {
                this.logger.debug('Was refresh successful?: ', result);
                this.isOnErrorPage = !result;
              },
              (err) => {
                this.isOnErrorPage = true;
                this.logger.error('Refresh encountered an error: ', err);
              },
            );
        }
      }
    });
  }

  ngOnDestroy() {
    this.switchedTokenSubscription?.unsubscribe();
    this.subscriptionRefresh?.unsubscribe();
    window.removeEventListener('message', this.onEyefinityLogoutMessage);
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
  }

  loadDynamicCss() {
    if (window.self !== window.top) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'http://localhost:8087/secure-eweb/common-web-tier/main.css';
      document.head.appendChild(link);
    }
  }

  /**
   * Displays the logout screen and loads an iframe to invalidate the PingFederate session.
   */
  logout() {
    this.isLoggingOut = true;
  }

  /**
   * Called when the logout iframe loads, indicating a successful logout in PingFederate.
   * At this point, pe-conn can safely clear the session storage.
   */
  performLogout() {
    this.authenticationManager.logout();
  }

  /**
   * Attempts login through authenticationManager
   */
  login() {
    this.authenticationManager.login();
  }

  /**
   * Host listener for the Document visibilitychange event.
   * When the document becomes visible again, delegate to the authentication
   * manager to reconcile session state (either reschedule token refresh or
   * redirect to the login flow if the session expired).
   */
  @HostListener('document:visibilitychange')
  onDocumentVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.authenticationManager.handleVisibilityResume();
    }
  }

  /**
   * Checks whether the user's route contains an EPM URL in order to display the EPM logo in the header.
   * Users from Encompass should also be considered for displaying the EPM logo.
   */
  isRoutingFromEpm(): boolean {
    if (this.router && this.router.url) {
      return (
        this.router.url.toString().includes(EPM_URL) ||
        this.router.url.toString().includes(ENCOMPASS_URL)
      );
    }
    return false;
  }
}
